import { brandStorage as localStorage, isHomeSmartPublicPage } from '@/utils/brandSession';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/config';

export const APP_VERSION = '2.5.0';

// Context
const TourContext = createContext(null);

// Hook per usare il tour
export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    // Return dummy functions if not in provider (for SSR/testing)
    return {
      startTour: () => {},
      stopTour: () => {},
      runTour: false,
      isTourCompleted: () => false,
      tourProgress: { completed: [] },
      currentTourId: null,
      APP_VERSION
    };
  }
  return context;
};

// Componente Tooltip personalizzato
const TourTooltip = ({ step, currentStep, totalSteps, onNext, onPrev, onSkip, onClose }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    const target = step.target === 'body' ? null : document.querySelector(step.target);
    
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
      
      // Calcola posizione tooltip
      const placement = step.placement || 'bottom';
      let top, left;
      
      switch (placement) {
        case 'top':
          top = rect.top - 10;
          left = rect.left + rect.width / 2;
          break;
        case 'bottom':
          top = rect.bottom + 10;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 10;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 10;
          break;
        default:
          top = rect.bottom + 10;
          left = rect.left + rect.width / 2;
      }
      
      setPosition({ top, left, placement });
      
      // Scroll to element
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Centro dello schermo per step "body"
      setPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
        placement: 'center'
      });
      setTargetRect(null);
    }
  }, [step]);

  const getTooltipStyle = () => {
    const { top, left, placement } = position;
    let transform = 'translate(-50%, 0)';
    
    if (placement === 'center') {
      transform = 'translate(-50%, -50%)';
    } else if (placement === 'left') {
      transform = 'translate(-100%, -50%)';
    } else if (placement === 'right') {
      transform = 'translate(0, -50%)';
    } else if (placement === 'top') {
      transform = 'translate(-50%, -100%)';
    }
    
    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform,
      zIndex: 10002,
      maxWidth: '400px',
      width: '90vw'
    };
  };

  return (
    <>
      {/* Overlay scuro */}
      <div className="fixed inset-0 bg-black/60 z-[10000]" onClick={onSkip} />
      
      {/* Spotlight sull'elemento target */}
      {targetRect && (
        <div
          className="fixed z-[10001] rounded-lg ring-4 ring-blue-500 ring-opacity-50"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
            pointerEvents: 'none'
          }}
        />
      )}
      
      {/* Tooltip */}
      <div style={getTooltipStyle()} className="bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <HelpCircle className="h-5 w-5" />
            <span className="font-medium">Guida</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-blue-100 text-sm">{currentStep + 1} / {totalSteps}</span>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {step.content}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
          <button
            onClick={onSkip}
            className="text-slate-500 hover:text-slate-700 text-sm"
          >
            Salta tour
          </button>
          
          <div className="flex gap-2">
            {currentStep > 0 && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 px-3 py-1.5 text-slate-600 hover:bg-slate-200 rounded-lg transition"
              >
                <ChevronLeft className="h-4 w-4" />
                Indietro
              </button>
            )}
            
            <button
              onClick={onNext}
              className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              {currentStep === totalSteps - 1 ? 'Fine' : 'Avanti'}
              {currentStep < totalSteps - 1 && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export const TourProvider = ({ children }) => {
  const [runTour, setRunTour] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const [currentTourId, setCurrentTourId] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourProgress, setTourProgress] = useState({
    completed: [],
    lastVersion: null,
    preferences: { autoStart: true }
  });
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [showHsWelcome, setShowHsWelcome] = useState(false);
  const [updateNotes, setUpdateNotes] = useState([]);
  
  const token = localStorage.getItem('hw_token');

  // Carica progresso tour dal server
  const loadTourProgress = useCallback(async () => {
    if (!token || isHomeSmartPublicPage()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/user/tour-progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        if (isHomeSmartPublicPage() || token !== localStorage.getItem('hw_token')) return;
        const data = await res.json();
        setTourProgress(data);
        
        // Rileva tenant esterno Home Smart: benvenuto dedicato, niente tour responsabile
        let isExternal = false;
        try {
          const vres = await fetch(`${API_URL}/api/auth/verify`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (vres.ok) isExternal = !!(await vres.json()).user?.is_external_tenant;
        } catch (e) { /* ignora */ }
        
        const isResponsabilePage = window.location.pathname.includes('/responsabile');
        if (isExternal) {
          if (!data.completed?.includes('hs_welcome') && isResponsabilePage) {
            setShowHsWelcome(true);
          }
          return;
        }
        
        // Controlla se mostrare popup aggiornamenti
        if (data.lastVersion && data.lastVersion !== APP_VERSION) {
          checkForUpdates(data.lastVersion);
        }
        
        // Se è la prima volta e siamo su una pagina responsabile, avvia tour
        if (!data.completed?.includes('first_time') && data.preferences?.autoStart !== false && isResponsabilePage) {
          setTimeout(() => {
            startTour('first_time');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error loading tour progress:', error);
    }
  }, [token]);

  // Salva progresso tour
  const saveTourProgress = useCallback(async (tourId, completed = false) => {
    if (!token) return;
    
    try {
      await fetch(`${API_URL}/api/user/tour-progress`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tourId,
          completed,
          appVersion: APP_VERSION
        })
      });
      
      if (completed) {
        setTourProgress(prev => ({
          ...prev,
          completed: [...(prev.completed || []), tourId],
          lastVersion: APP_VERSION
        }));
      }
    } catch (error) {
      console.error('Error saving tour progress:', error);
    }
  }, [token]);

  // Controlla aggiornamenti
  const checkForUpdates = (lastVersion) => {
    const updates = getUpdateNotes(lastVersion, APP_VERSION);
    if (updates.length > 0) {
      setUpdateNotes(updates);
      setShowUpdatePopup(true);
    }
  };

  // Avvia un tour specifico
  const startTour = useCallback((tourId, steps = null) => {
    const tourStepsToUse = steps || getTourSteps(tourId);
    if (tourStepsToUse.length === 0) return;
    
    setCurrentTourId(tourId);
    setTourSteps(tourStepsToUse);
    setCurrentStep(0);
    setRunTour(true);
  }, []);

  // Ferma il tour
  const stopTour = useCallback(() => {
    setRunTour(false);
    setCurrentTourId(null);
    setCurrentStep(0);
  }, []);

  // Handlers per navigazione tour
  const handleNext = useCallback(() => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tour completato
      saveTourProgress(currentTourId, true);
      toast.success('Tour completato! Puoi rivederlo cliccando su "Guida" nel menu.');
      stopTour();
    }
  }, [currentStep, tourSteps.length, currentTourId, saveTourProgress, stopTour]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    saveTourProgress(currentTourId, true);
    toast.info('Tour saltato. Puoi rivederlo quando vuoi cliccando su "Guida".');
    stopTour();
  }, [currentTourId, saveTourProgress, stopTour]);

  // Verifica se un tour è stato completato
  const isTourCompleted = useCallback((tourId) => {
    return tourProgress.completed?.includes(tourId);
  }, [tourProgress.completed]);

  // Chiudi popup aggiornamenti
  const closeUpdatePopup = useCallback(() => {
    setShowUpdatePopup(false);
    saveTourProgress('update_' + APP_VERSION, true);
  }, [saveTourProgress]);

  useEffect(() => {
    loadTourProgress();
  }, [loadTourProgress]);

  const value = {
    startTour,
    stopTour,
    runTour,
    isTourCompleted,
    tourProgress,
    currentTourId,
    APP_VERSION
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      
      {/* Tour tooltip */}
      {runTour && tourSteps.length > 0 && createPortal(
        <TourTooltip
          step={tourSteps[currentStep]}
          currentStep={currentStep}
          totalSteps={tourSteps.length}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={handleSkip}
          onClose={handleSkip}
        />,
        document.body
      )}
      
      {/* Popup aggiornamenti */}
      {showUpdatePopup && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                🎉 Novità nell'app!
              </h2>
              <p className="text-blue-100 text-sm mt-1">Versione {APP_VERSION}</p>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              <ul className="space-y-3">
                {updateNotes.map((note, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="text-xl">{note.icon}</span>
                    <div>
                      <p className="font-medium text-slate-800">{note.title}</p>
                      <p className="text-sm text-slate-600">{note.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-6 py-4 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={closeUpdatePopup}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Chiudi
              </button>
              <button
                onClick={() => {
                  closeUpdatePopup();
                  startTour('updates_' + APP_VERSION);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Mostra tour novità
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Benvenuto Home Smart (tenant esterni) */}
      {showHsWelcome && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10001] p-4" data-testid="hs-welcome-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 pt-8 pb-4 text-center">
              <img src="/home-smart-logo.png" alt="Home Smart" className="h-24 w-24 mx-auto mb-3 object-contain" />
              <h2 className="text-2xl font-bold text-slate-800">Benvenuto in Home Smart! 👋</h2>
              <p className="text-slate-500 text-sm mt-1">La tua area operativa personale</p>
            </div>
            <div className="px-8 py-2">
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex gap-3 items-start"><span className="text-lg">🏠</span><span><b>Immobili</b>: le tue schede con tutti i dettagli e le credenziali per gli adempimenti</span></li>
                <li className="flex gap-3 items-start"><span className="text-lg">📅</span><span><b>Prenotazioni e calendario</b>: lo storico, gli arrivi e le partenze dei tuoi ospiti</span></li>
                <li className="flex gap-3 items-start"><span className="text-lg">🏛️</span><span><b>Adempimenti</b>: ricevute Questura e dati sempre in ordine</span></li>
                <li className="flex gap-3 items-start"><span className="text-lg">🔒</span><span><b>Solo i tuoi dati</b>: vedi esclusivamente i tuoi immobili e le tue prenotazioni</span></li>
              </ul>
            </div>
            <div className="px-6 py-5 flex justify-center">
              <button
                onClick={() => {
                  setShowHsWelcome(false);
                  saveTourProgress('hs_welcome', true);
                }}
                data-testid="hs-welcome-start-btn"
                className="px-8 py-2.5 text-white rounded-full font-semibold hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#1947C7' }}
              >
                Inizia
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </TourContext.Provider>
  );
};

// Definizione step tour per ogni pagina/funzionalità
function getTourSteps(tourId) {
  const tours = {
    // Tour prima volta - completo
    first_time: [
      {
        target: 'body',
        content: (
          <div>
            <h3 className="font-bold text-lg mb-2">👋 Benvenuto nel Portale Responsabile!</h3>
            <p>Questo breve tour ti guiderà attraverso le funzionalità principali del sistema.</p>
            <p className="mt-2 text-sm text-slate-500">Puoi saltare il tour in qualsiasi momento e rivederlo cliccando su "Guida" nel menu.</p>
          </div>
        ),
        placement: 'center',
      },
      {
        target: '[data-testid="sidebar-menu"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">📱 Menu di Navigazione</h3>
            <p>Da qui puoi accedere a tutte le sezioni:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• <b>Dashboard</b> - Panoramica arrivi e partenze</li>
              <li>• <b>Chiusure</b> - Periodi di chiusura immobili</li>
              <li>• <b>Ticket</b> - Comunicazioni con ufficio e proprietari</li>
              <li>• <b>Storico</b> - Archivio prenotazioni</li>
            </ul>
          </div>
        ),
        placement: 'right',
      },
      {
        target: '[data-testid="dashboard-stats"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">📊 Arrivi e Partenze</h3>
            <p>Questi pulsanti mostrano:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• <b className="text-green-600">Arrivi</b> - Ospiti in arrivo nel periodo</li>
              <li>• <b className="text-orange-600">Partenze</b> - Ospiti in partenza</li>
            </ul>
            <p className="mt-2 text-sm">Clicca su un pulsante per vedere i dettagli.</p>
          </div>
        ),
        placement: 'bottom',
      },
      {
        target: '[data-testid="period-filter"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">📅 Filtro Periodo</h3>
            <p>Seleziona il periodo da visualizzare:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• <b>48h</b> - Ultime 48 ore</li>
              <li>• <b>Oggi</b> - Solo oggi</li>
              <li>• <b>Domani</b> - Solo domani</li>
              <li>• <b>7gg</b> - Prossimi 7 giorni</li>
              <li>• <b>Mese</b> - Tutto il mese</li>
            </ul>
          </div>
        ),
        placement: 'bottom',
      },
      {
        target: '[data-testid="filters-section"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">🔍 Filtri Avanzati</h3>
            <p>Usa questi filtri per trovare rapidamente le prenotazioni:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• <b>Immobile</b> - Filtra per proprietà</li>
              <li>• <b>Stato Documenti</b> - Completi/Mancanti</li>
              <li>• <b>Stato Questura</b> - Inviato/Da inviare</li>
            </ul>
          </div>
        ),
        placement: 'bottom',
      },
      {
        target: '[data-testid="nav-chiusure"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">🔒 Chiusure Proprietà</h3>
            <p>In questa sezione vedi i periodi di chiusura degli immobili.</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• Riceverai email per ogni nuova chiusura</li>
              <li>• Leggi sempre il <b>promemoria importante</b></li>
              <li>• Ricorda: <b>foto prima di pulire</b> se l'immobile è sporco!</li>
            </ul>
          </div>
        ),
        placement: 'right',
      },
      {
        target: '[data-testid="nav-ticket"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">🎫 Sistema Ticket</h3>
            <p>Usa i ticket per comunicare con:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• <b>Ufficio HWsmart</b> - Per supporto e richieste</li>
              <li>• <b>Proprietari</b> - Per questioni sugli immobili</li>
              <li>• <b>Responsabili</b> - Per coordinamento</li>
            </ul>
            <p className="mt-2 text-sm text-amber-600 font-medium">⚠️ Includi sempre l'ufficio per accordi ufficiali!</p>
          </div>
        ),
        placement: 'right',
      },
      {
        target: '[data-testid="btn-guide-tour"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">❓ Rivedi la Guida</h3>
            <p>Puoi rivedere questo tour in qualsiasi momento cliccando su <b>"Guida"</b> qui nel menu.</p>
            <p className="mt-2 text-sm text-slate-500">Il tour è terminato. Buon lavoro! 🎉</p>
          </div>
        ),
        placement: 'right',
      },
    ],
    
    // Tour aggiornamenti v2.5.0
    'updates_2.5.0': [
      {
        target: 'body',
        content: (
          <div>
            <h3 className="font-bold text-lg mb-2">🆕 Novità versione 2.5.0</h3>
            <p>Ecco le nuove funzionalità che abbiamo aggiunto!</p>
          </div>
        ),
        placement: 'center',
      },
      {
        target: '[data-testid="ticket-view-toggle"]',
        content: (
          <div>
            <h3 className="font-bold mb-2">📋 Vista Tabella Ticket</h3>
            <p>Ora puoi visualizzare i ticket in formato tabella con:</p>
            <ul className="mt-2 text-sm space-y-1">
              <li>• Colonne ordinabili</li>
              <li>• Filtri per immobile e categoria</li>
              <li>• Vista più compatta</li>
            </ul>
          </div>
        ),
        placement: 'bottom',
      },
    ],
  };
  
  return tours[tourId] || [];
}

// Note aggiornamenti tra versioni
function getUpdateNotes(fromVersion, toVersion) {
  const allUpdates = {
    '2.5.0': [
      {
        icon: '🎫',
        title: 'Sistema Ticket Migliorato',
        description: 'Filtri avanzati per immobile, categoria e responsabile. Vista tabella con colonne ordinabili.'
      },
      {
        icon: '📊',
        title: 'Vista Tabella per Ticket',
        description: 'Passa dalla vista lista alla vista tabella per una panoramica più completa.'
      },
      {
        icon: '⚠️',
        title: 'Disclaimer Accordi Privati',
        description: 'Quando invii un ticket senza coinvolgere l\'ufficio, vedrai un avviso importante.'
      },
      {
        icon: '🔔',
        title: 'Notifiche Chiusure Automatiche',
        description: 'Ricevi email automatiche quando vengono registrate nuove chiusure.'
      },
      {
        icon: '❓',
        title: 'Tour Guidato',
        description: 'Ora hai una guida interattiva che spiega tutte le funzionalità. Clicca su "Guida" nel menu per rivederla.'
      },
    ],
  };
  
  const updates = [];
  const versions = Object.keys(allUpdates).sort();
  
  for (const version of versions) {
    if (version > fromVersion && version <= toVersion) {
      updates.push(...allUpdates[version]);
    }
  }
  
  return updates;
}

export default TourProvider;
